// 보스 공격 VFX 공용 라이브러리(C2): 내려찍기 충격파 · 부채꼴 브레스 · 돌진 궤적 · 낙하 운석 · 장판 — 위치·각·반경·색·지속을 받는 순수 함수.
// 레이드 VFX 모듈 시스템(src/vfx/modules, VfxModules) 위에 얹혀 있어 어느 보스(C7 서리 거인 등)든 같은 호출로 재사용한다.
// 이 라이브러리는 그리기만 한다: 판정·텔레그래프(경고 표시)는 건드리지도 만들지도 않는다(telegraph.ts가 맡고, 판정 모양과 같다는 불변식은 그쪽).
// 규약: 시간은 초, 길이는 m, 각도는 rad, yaw 방향 벡터는 (sin yaw, cos yaw). 반환값 = 띄운 층 수(모듈이 가득 차 거부한 층은 세지 않는다).
// 한 번 호출 = 보스 공격 한 번(이벤트 시점)이라 프리셋 객체를 그때 만든다(VfxModules가 지연 층의 프리셋을 참조로 들고 있어 공유 객체를 쓸 수 없다). 프레임 루프에서 부르지 않는다.
import type { FxPreset, ModuleKind } from '../modules/types';

/** VfxModules.play의 최소 모양(테스트·프리뷰는 이 모양만 구현하면 된다) */
export interface BossFxPlayer {
  play(kind: ModuleKind, preset: FxPreset, x: number, y: number, z: number, yaw: number, delay?: number, lag?: number): boolean;
}

/** 색 한 쌍: 주색(몸통) + 흰 심(밝은 쪽) */
export interface BossFxColors { readonly color: number; readonly color2: number }

class Layers {
  n = 0;
  constructor(private readonly fx: BossFxPlayer) {}
  add(kind: ModuleKind, p: FxPreset, x: number, y: number, z: number, yaw: number, delay = 0): void {
    if (this.fx.play(kind, p, x, y, z, yaw, delay)) this.n++;
  }
}

/**
 * 내려찍기 충격파: 땅을 치는 순간 바닥에서 퍼지는 고리 둘(안쪽 늦게) + 방사 균열 + 먼지 섞인 폭발.
 * radius = 판정 반지름(고리가 도달하는 곳), life = 바깥 고리 지속.
 * bodyRadius > 0이면 보스 몸(반지름 m)에 가려지지 않게 고리를 몸 가장자리 바깥에서 시작해 두껍게 퍼지고, 가운데 한 곳에 몰려 몸에 묻히던 폭발은
 * 충격파 둘레(네 곳)로 옮겨 띄운다. 이때 층 수는 4가 아니라 더 많아질 수 있다(모듈이 가득 차면 거부된 층은 세지 않는다).
 */
export function slamShockwave(fx: BossFxPlayer, x: number, z: number, radius: number, colors: BossFxColors, life = 0.6, bodyRadius = 0): number {
  const L = new Layers(fx), R = Math.max(1, radius);
  if (bodyRadius <= 0) {
    L.add('ring', { length: R, from: 0.15, width: R * 0.2, life, color: colors.color, color2: colors.color2, intensity: 2.0, seed: 0.1 }, x, 0, z, 0);
    L.add('ring', { length: R * 0.72, from: 0.1, width: R * 0.14, life: life * 0.8, color: colors.color2, color2: colors.color, intensity: 1.3, seed: 0.5 }, x, 0, z, 0, 0.07);
    L.add('crack', { variant: 1, length: R * 0.9, life: life * 2.2, color: colors.color, color2: colors.color2, intensity: 1.5, seed: 0.3 }, x, 0, z, 0);
    L.add('blast', { variant: 1, y: R * 0.14, length: R * 0.95, life: life * 1.2, color: colors.color, color2: colors.color2, intensity: 1.5, seed: 0.2 }, x, 0, z, 0);
    return L.n;
  }
  // 몸 바깥에서 시작: 시작 비율 = 몸 반지름 / 충격파 반지름(너무 크면 띠가 얇아지니 최대 0.72)
  const f = Math.min(0.72, Math.max(0.15, (bodyRadius * 1.05) / R));
  L.add('ring', { length: R, from: f, width: R * 0.34, life, color: colors.color, color2: colors.color2, intensity: 2.6, seed: 0.1 }, x, 0, z, 0);
  L.add('ring', { length: R * 0.82, from: f * 0.92, width: R * 0.2, life: life * 0.85, color: colors.color2, color2: colors.color, intensity: 1.7, seed: 0.5 }, x, 0, z, 0, 0.07);
  L.add('crack', { variant: 1, length: R * 0.95, life: life * 2.2, color: colors.color, color2: colors.color2, intensity: 1.7, seed: 0.3 }, x, 0, z, 0);
  // 둘레 먼지 폭발: 충격파 가장자리 근처 네 곳(모듈 칸 6 중 4만 쓴다)(보스 몸 뒤에 가려지지 않는 바깥)
  const rim = Math.max(bodyRadius * 1.15, R * 0.78);
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + 0.4;
    L.add('blast', { variant: 1, y: R * 0.16, length: R * 0.62, life: life * 1.3, color: colors.color, color2: colors.color2, intensity: 1.6, seed: 0.2 + i * 0.13 }, x + Math.sin(a) * rim, 0, z + Math.cos(a) * rim, 0, 0.03 * i);
  }
  return L.n;
}

/**
 * 부채꼴 브레스: 입(x,z)에서 yaw 방향으로 range, 각 arc(rad)로 퍼지는 부채꼴 장판 + 빔 셋(갈래) + 입 앞 폭발 + 불덩이 줄기.
 * life = 장판·빔 지속
 */
export function breathCone(fx: BossFxPlayer, x: number, z: number, yaw: number, range: number, arc: number, colors: BossFxColors, life = 0.9): number {
  const L = new Layers(fx), Rg = Math.max(2, range), A = Math.max(0.3, arc);
  L.add('zone', { variant: 1, sweep: A, length: Rg, life, color: colors.color, color2: colors.color2, intensity: 1.5, seed: 0.2 }, x, 0, z, yaw);
  const spread = A * 0.3;
  for (let i = -1; i <= 1; i++) {
    L.add('beam', { y: 1.0, ahead: 0.7, length: Rg, width: Math.max(0.5, Rg * 0.1), life: life * 0.75, color: colors.color, color2: colors.color2, intensity: 1.4, seed: 0.3 + i * 0.2 }, x, 0, z, yaw + i * spread, 0.05 * (i + 1));
    L.add('projectile', { y: 1.0, ahead: 0.8, length: Rg, width: 0.9, tail: Rg * 0.3, life: life * 0.55, color: colors.color, color2: colors.color2, intensity: 1.6, seed: 0.4 + i * 0.1 }, x, 0, z, yaw + i * spread, 0.12 * (i + 1));
  }
  L.add('blast', { y: 1.2, ahead: 1.2, length: Math.max(2.5, Rg * 0.4), life: life * 0.7, color: colors.color, color2: colors.color2, intensity: 1.7, seed: 0.1 }, x, 0, z, yaw);
  return L.n;
}

/**
 * 돌진 궤적: 출발점(x,z)에서 yaw 방향 distance까지 속도선 셋(높이 다르게) + 지나간 자리에 앞쪽으로 갈라진 균열 + 도착 충격 고리.
 * width = 몸 폭(m), life = 속도선 지속(도착 고리는 돌진이 끝나는 90% 지점)
 */
export function chargeStreak(fx: BossFxPlayer, x: number, z: number, yaw: number, distance: number, width: number, colors: BossFxColors, life = 0.45): number {
  const L = new Layers(fx), D = Math.max(2, distance), W = Math.max(0.6, width);
  const lanes: readonly number[] = [0.4, 0.95, 1.5];
  for (let i = 0; i < lanes.length; i++) {
    L.add('trail', { variant: 0, y: lanes[i], length: D, width: W * (0.28 - 0.04 * i), life, tail: life * 0.35, color: colors.color, color2: colors.color2, intensity: 2.0, seed: 0.1 + i * 0.3 }, x, 0, z, yaw + (i - 1) * 0.02);
  }
  L.add('crack', { variant: 0, ahead: D * 0.15, length: D * 0.85, width: W * 1.6, life: life * 3, color: colors.color, color2: colors.color2, intensity: 1.3, seed: 0.6 }, x, 0, z, yaw);
  const ex = x + Math.sin(yaw) * D, ez = z + Math.cos(yaw) * D;
  L.add('ring', { length: W * 1.8, from: 0.3, width: W * 0.35, life: 0.4, color: colors.color, color2: colors.color2, intensity: 1.6, seed: 0.8 }, ex, 0, ez, 0, life * 0.9);
  return L.n;
}

/**
 * 낙하 운석: 하늘에서 비스듬히 떨어지는 꼬리(수직면 아크, 끝이 (x,z)) → fall초 뒤 착탄 고리 + 폭발 + 균열 + 짧은 불기둥.
 * radius = 착탄 반경, dirYaw = 날아오는 쪽을 가리키는 방향(꼬리가 그 반대편에서 온다)
 */
export function meteorFall(fx: BossFxPlayer, x: number, z: number, radius: number, fall: number, colors: BossFxColors, dirYaw = 0.8): number {
  const L = new Layers(fx), R = Math.max(1, radius), T = Math.max(0.2, fall);
  const reach = Math.max(7, R * 3);
  // 아크 중심 = 착탄점에서 날아오는 방향 반대로 reach만큼. 아크가 위(1.25rad)에서 착탄점(0rad)까지 휘어 내려온다
  const cx = x - Math.sin(dirYaw) * reach, cz = z - Math.cos(dirYaw) * reach;
  L.add('trail', { variant: 1, roll: Math.PI / 2, y: 0.1, length: reach, width: Math.max(0.8, R * 0.35), start: 1.25, sweep: -1.25, life: T, tail: T * 0.55, color: colors.color, color2: colors.color2, intensity: 2.2, seed: 0.2 }, cx, 0, cz, dirYaw);
  L.add('trail', { variant: 1, roll: Math.PI / 2, y: 0.1, length: reach * 0.96, width: Math.max(0.5, R * 0.2), start: 1.3, sweep: -1.3, life: T, tail: T * 0.45, color: colors.color2, color2: colors.color, intensity: 2.0, seed: 0.7 }, cx, 0, cz, dirYaw + 0.04);
  L.add('ring', { length: R, from: 0.2, width: R * 0.2, life: 0.55, color: colors.color, color2: colors.color2, intensity: 2.0, seed: 0.3 }, x, 0, z, 0, T);
  L.add('blast', { y: R * 0.3, length: R * 1.3, life: 0.8, color: colors.color, color2: colors.color2, intensity: 1.8, seed: 0.5 }, x, 0, z, 0, T);
  L.add('crack', { variant: 1, length: R * 0.85, life: 1.4, color: colors.color, color2: colors.color2, intensity: 1.5, seed: 0.9 }, x, 0, z, 0, T);
  L.add('pillar', { length: R * 1.8, width: R * 0.25, life: 0.6, color: colors.color, color2: colors.color2, intensity: 1.4, seed: 0.4 }, x, 0, z, 0, T);
  return L.n;
}

/**
 * 장판: 바닥에 life초 머무는 영역(원 또는 부채꼴 arc>0) + (원일 때) 가장자리에서 숨 쉬듯 번지는 고리 셋.
 * 판정 영역은 호출하는 쪽이 정한다(이 함수는 같은 반경으로 그리기만 한다)
 */
export function groundZone(fx: BossFxPlayer, x: number, z: number, radius: number, life: number, colors: BossFxColors, arc = 0, yaw = 0): number {
  const L = new Layers(fx), R = Math.max(1, radius), T = Math.max(0.3, life);
  if (arc > 0) L.add('zone', { variant: 1, sweep: arc, length: R, life: T, color: colors.color, color2: colors.color2, intensity: 1.5, seed: 0.3 }, x, 0, z, yaw);
  else L.add('zone', { variant: 0, length: R, life: T, color: colors.color, color2: colors.color2, intensity: 1.5, seed: 0.3 }, x, 0, z, 0);
  for (let i = 0; i < 3 && arc <= 0; i++) {
    L.add('ring', { length: R * 0.96, from: 0.8, width: R * 0.08, life: 0.5, color: colors.color2, color2: colors.color, intensity: 0.9, seed: 0.2 + i * 0.3 }, x, 0, z, yaw, (T * i) / 3);
  }
  return L.n;
}
